import { InfoRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoSharpIcon from '@mui/icons-material/InfoSharp';
import * as Mui from '@mui/material';
import * as MuiIcons from "@mui/icons-material";
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import useOperationTranslation from '../../hooks/useOperationTranslation';
import AutoComplete from '../common/AutoComplete';

import VehicleEntranceService from './service/VehicleEntranceService';

import { AxiosError } from 'axios';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../../../util/state/GlobalContextProvider';
import { ContextOperation } from '../../context/OperationContext';
import useSnackbar from '../../hooks/useSnackbar';
import OperationTypes from "../../types";
import CheckInOutWrapper from "../common/CheckInOutWrapper";
import { IExternalDDTConfirmFormInputs, IExternalDDTDialogForm, IExternalDDTVehicleData, ILocation, TransferRequestForExternalDTTResponse } from './types';
import { formatTypeString } from './utils';
import EnumChangeName from '../common/EnumChangeName';
import OpenTransferPlatesDialog from './dialogs/OpenTransferPlatesDialog';

type VehicleEntranceExternalDDTType = OperationTypes.Vehicle & {
    position: string | null;
    departureKm: number;
    departureThermicFuel: number;
    departureElectricLevel: number;
    formDamageIds?: number[];
    existingOpenDamageIds?: number[];
    existingOpenDamageEquipmentIds?: number[];
};

const VehicleEntranceExternalDDTConfirm = () => {
    const { tenant, setLoading } = useContext(ContextOperation);

    const navigate = useNavigate();
    const location = useLocation();
    const snackbar = useSnackbar();
    const vehicleData = location.state as IExternalDDTVehicleData;
    const t = useOperationTranslation();
    const { i18n } = useTranslation();
    const form1 = useForm<IExternalDDTConfirmFormInputs>({
        defaultValues: {
            position: vehicleData.lms,
            transferType: null,
        },
    });

    const form2 = useForm<IExternalDDTDialogForm>({
        defaultValues: {
            plate: null,
            position: null,
        },
    });

    const [plates, setPlates] = useState<VehicleEntranceExternalDDTType[]>([]);
    const [visibleDialog, setVisibleDialog] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [drivers, setDrivers] = useState([]);
    const [allDrivers, setAllDrivers] = useState([]);
    const [allPlates, setAllPlates] = useState([]);
    const [open, setOpen] = useState<any>(false);
    const [dialogData, setDialogData] = useState<any>({});
    const [allLocations, setAllLocations] = useState<any>([]);
    const { globalData } = useContext(GlobalContext);
    const [user, setUser] = useState<any>(globalData.user);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [deliveryNoteNumber, setDeliveryNoteNumber] = useState(null);

    const [selectedVehicle, setSelectedVehicle] = useState<OperationTypes.Vehicle | null>(null);
    const [checkedInVehicles, setCheckedInVehicles] = useState<(string | undefined)[]>([]);

    const [transferPlatesDialog, setTransferPlatesDialog] = useState<{
        open: boolean,
        data: TransferRequestForExternalDTTResponse[] | []
    }>({
        open: false,
        data: []
    });

    const { locationType } = EnumChangeName();

    const onOpenCheckIn = (vehicle: VehicleEntranceExternalDDTType) => {
        //Get all existing missing equipments
        VehicleEntranceService.get.missingEquipments(tenant, vehicle?.plate || "").then(
            response => {
                // update vehicles with existing missing equipments
                const newVehicles = plates.map((plateVehicle) => {
                    if (plateVehicle.plate === vehicle?.plate) {
                        const newVehicle: VehicleEntranceExternalDDTType = {
                            ...plateVehicle,
                            existingOpenDamageEquipmentIds: response.content.map((element) => element.id),
                        };

                        return newVehicle;
                    }
                    return plateVehicle;
                });
                setPlates(newVehicles);

                setSelectedVehicle(vehicle);
            },
            error => {
                snackbar.show("error", t("vehicleEntrance.externalDDT.errorMissingEquipments"));
            }
        );
    }

    const onConfirmCheckIn = (ret: OperationTypes.CheckInOutType.retObject) => {
        // update already checked in vehicles
        setCheckedInVehicles([
            ...checkedInVehicles,
            selectedVehicle?.plate
        ]);

        // update checked in vehicles with 1) damages and 2) new missing equipments
        const newVehicles = plates.map((vehicle) => {
            if (vehicle.plate === selectedVehicle?.plate) {
                const newVehicle: VehicleEntranceExternalDDTType = {
                    ...vehicle,
                    formDamageIds: [
                        ...ret.eventsExternal.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
                        ...ret.eventsInternal.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
                    ],
                    existingOpenDamageIds: [
                        ...ret.existingDamages.external.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
                        ...ret.existingDamages.internal.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
                    ],
                };
    
                if (ret.equipmentsEvent.id !== null && ret.equipmentsEvent.id !== undefined) {
                    if (!newVehicle.formDamageIds) {
                        newVehicle.formDamageIds = [];
                    }
                    
                    newVehicle.formDamageIds.push(ret.equipmentsEvent.id);
                }
                return newVehicle;
            }
            return vehicle;
        });
        setPlates(newVehicles);

        setSelectedVehicle(null);
    }

    const [types, setTypes] = useState<any>([
        {
            label: t('vehicleEntrance.withoutPlate.carsData.carTransport'),
            value: 'CAR_TRANSPORTER',
        },
        {
            label: t('vehicleEntrance.withoutPlate.carsData.carDriver'),
            value: 'DRIVER',
        },
    ]);

    useMemo(() => {
        VehicleEntranceService.get.allDrivers(vehicleData?.tenant).then((response) => setAllDrivers(response));

        if (vehicleData.locationStart.type === 'SHOP_REPAIR') {
            VehicleEntranceService.get
                .repariShopPlatesByReferenceLocationIdWithDdt(vehicleData?.tenant, vehicleData.locationStart.bpCode)
                .then((response) => {
                    setAllPlates(response);
                });
        } else {
            VehicleEntranceService.get
                .platesByReferenceLocationIdWithDdt(vehicleData?.tenant, vehicleData.locationStart.bpCode, vehicleData.locationStart.type)
                .then((response) => setAllPlates(response));
        }

        if (vehicleData) {
            setAllLocations(
                vehicleData?.allLocations.map((item: any) => {
                    return {
                        ...item,
                        label: `${item.locationEntityDesc} (${formatTypeString(item.type, t)})`,
                        value: item.locationEntityDesc,
                    };
                }),
            );

            setTypes([
                {
                    label: t('vehicleEntrance.withoutPlate.carsData.carTransport'),
                    value: 'CAR_TRANSPORTER',
                },
                {
                    label: t('vehicleEntrance.withoutPlate.carsData.carDriver'),
                    value: 'DRIVER',
                },
            ]);
        }
    }, [vehicleData?.tenant, i18n?.language]);

    const handleConfirmEntrance = (data: IExternalDDTConfirmFormInputs, oldTransferRequestsNumbers?: string[]) => {
        const startStation = vehicleData.allLocations.find(loc => loc.bpCode === vehicleData.locationStart.bpCode && loc.type === vehicleData.locationStart.type);
        const expectedEndStation = vehicleData.allLocations.find(loc => loc.bpCode === vehicleData.locationEnd.bpCode && loc.type === vehicleData.locationEnd.type);
        const endStation = vehicleData.allLocations.find(loc => loc.bpCode === vehicleData.effectiveEndLocation.bpCode && loc.type === vehicleData.effectiveEndLocation.typeEnum);
        
        VehicleEntranceService.add
            .confirmEntrance(vehicleData.tenant, {
                startStationId: startStation?.bpCode,
                startStationType: startStation?.type,
                transferType: data.transferType,
                startDate: vehicleData.startDate,
                expectedEndStationId: expectedEndStation?.bpCode,
                expectedEndStationType: expectedEndStation?.type,
                expectedEndDate: vehicleData.expectedEndDate,
                endStationId: endStation?.bpCode,
                endStationType: endStation?.type,
                endDate: vehicleData.endDate,
                note: '',
                externalNumber: vehicleData.deliveryNoteNumber,
                operatorCode: user.username,
                driversId: drivers.map((driver: any) => driver.id),
                vehicles: plates.map((vehicle: VehicleEntranceExternalDDTType) => {
                    return {
                        plate: vehicle.plate,
                        position: vehicle.position,
                        departureKm: vehicle.departureKm,
                        departureThermicFuel: vehicle.departureThermicFuel,
                        departureElectricLevel: vehicle.departureElectricLevel,
                        formDamageIds: vehicle.formDamageIds,
                        existingOpenDamageIds: vehicle.existingOpenDamageIds,
                        existingOpenDamageEquipmentIds: vehicle.existingOpenDamageEquipmentIds
                    };
                }),
                reasonCode: vehicleData.reasonCode,
                oldTransferRequestsNumbers: oldTransferRequestsNumbers
            })
            .then(
                (response) => {
                    setLoading(false);
                    snackbar.show(
                        'success',
                        t('vehicleEntrance.withPlate.confirm.success').replace(
                            '#plateNotFound',
                            vehicleData.deliveryNoteNumber,
                        ),
                    );

                    setDeliveryNoteNumber(response.deliveryNoteId);
                    setShowDownloadModal(true);
                },
                (reject : AxiosError<OperationTypes.AxiosErrorResponse>) => {
                    setLoading(false);
                    if (reject.response?.status === 400 && reject.response?.data.subCode === 11) {
                        snackbar.show("error", t("vehicleEntrance.externalDDT.400ErrorSubCode11"));
                    }
                    else if (reject.response?.status === 400) {
                        snackbar.show("error", t("vehicleEntrance.externalDDT.400Error"));
                    }
                    else if (reject.response?.status === 404) {
                        snackbar.show("error", t("vehicleEntrance.externalDDT.404Error"));
                    }
                    else if (reject.response?.status === 409) {
                        snackbar.show("error", t("vehicleEntrance.externalDDT.409Error"));
                    }
                    else {
                        snackbar.show("error", t("vehicleEntrance.externalDDT.500Error"));
                    }
                }
        );
    }

    const onSubmit = (data: IExternalDDTConfirmFormInputs) => {     

        const openTransferPlates = plates.map(p => p.plate).filter(p => !!p);
        if(openTransferPlates.length == 0) {
            setOpen(false);
            snackbar.show("error", t('vehicleEntrance.openTransferPlates.error.noVehiclesSelected'));
            return;
        }

        setLoading(true);
        VehicleEntranceService.get
            .openTransferRequestsByPlatesInTFWORK(vehicleData.tenant, plates.map(p => p.plate).filter(p => !!p))
            .then(
                (response) => {
                    if(response.length > 0) {
                        setLoading(false);
                        setOpen(false);
                        setTransferPlatesDialog({
                            open: true,
                            data: response
                        })
                    }
                    else {
                        handleConfirmEntrance(data)
                    }
                    
                },
                (reject : AxiosError<OperationTypes.AxiosErrorResponse>) => {
                    setLoading(false);
                    snackbar.show("error", t("vehicleEntrance.externalDDT.500Error"));
                },
            );
        
    };

    const onPlateAddSubmit = (data: IExternalDDTDialogForm) => {
        setLoading(true);
        VehicleEntranceService.get
            .vehicleByPlate(vehicleData.tenant, form2.watch(`plate`) || '')
            .then(
                (response: OperationTypes.Vehicle) => {
                    console.log(response);
                    setLoading(false);
                    setPlates([
                        ...plates,
                        {
                            ...response,
                            position: form2.watch("position"),
                            departureKm: response.kms,
                            departureElectricLevel: response.currentElectricTankFilling,
                            departureThermicFuel: response.currentTermicTankFilling
                        },
                    ]);
                    form2.reset();
                    setVisibleDialog(false);
                },
                (reject) => {
                    setLoading(false);
                    snackbar.show(
                        'warning',
                        t('vehicleExit.search.notFound').replace('#plateNotFound', form2.watch(`plate`) as string),
                    );
                },
            )
            .catch(() => {
                setLoading(false);
                snackbar.show('error', t('vehicleExit.confirm.genericError'));
            });
    };

    const locationStartValue = `${vehicleData.locationStart.locationEntityDesc} (${locationType(vehicleData.locationStart.type)})`;

    const locationEndValue = `${vehicleData.locationEnd.locationEntityDesc} (${locationType(vehicleData.locationEnd.type)})`;

    const checkDrivers = () => {
        setOpen(true);
        setDialogData({
            title: '',
            bodyTitle: '',
            body: t('vehicleEntrance.externalDDT.dialog.body'),
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const allPlatesOptions = () => {
        const selectedPlates = plates.map((item: VehicleEntranceExternalDDTType) => item.plate);

        const availablePlates = allPlates.filter(({ plate }: any) => {
            return !selectedPlates.find((item: string) => item === plate);
        });

        return availablePlates;
    };

    const downloadDeliveryNote = () => {
        setLoading(true);
        const confirmTenant: string = (vehicleData as { tenant: string }).tenant;
        VehicleEntranceService.get
            .downloadDeliveryNote(confirmTenant, deliveryNoteNumber)
            .then((response: any) => {
                setLoading(false);
                const filename = response.headers['content-disposition'].split('filename=')[1];
                const blob = new Blob([response.data], { type: 'application/pdf' });
                FileSaver.saveAs(blob, filename);

                navigate(`/operation/transfer/vehiclesEntrance`);
            })
            .catch(() => {
                snackbar.show('error', t('vehicleExit.confirm.genericError'));
            });
    };

    return (
        <>
            <Mui.Box padding={{ md: 2 }}>
                {!selectedVehicle && (
                    <Mui.Paper>
                        <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
                            <Mui.Grid item md={6} xs={12}>
                                <Mui.Card>
                                    <Mui.CardContent>
                                        <Mui.Grid container alignItems={"center"}>
                                            <Mui.Grid item xs={2}>
                                                <Mui.IconButton onClick={() => navigate("/operation/transfer/vehiclesEntrance/externalDDT", { state: {...vehicleData, page: "externalDDTConfirm"} })}>
                                                    <MuiIcons.ArrowBack />
                                                </Mui.IconButton>
                                            </Mui.Grid>
                                            <Mui.Grid item xs={8}>
                                                <Mui.Typography variant="h4" whiteSpace="pre-line" textAlign="center" p={2}>
                                                    {t('vehicleEntrance.withoutPlate.carsData.headerTitle')}
                                                </Mui.Typography>
                                            </Mui.Grid>
                                        </Mui.Grid>
                                        <Mui.Box
                                            sx={{
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'center',
                                            }}
                                            pt="20px"
                                        >
                                            <Mui.Typography variant="body1">
                                                {t('vehicleEntrance.withoutPlate.carsData.referenceTitle')}:
                                            </Mui.Typography>
                                            <Mui.Typography variant="h5">{vehicleData.deliveryNoteNumber}</Mui.Typography>
                                        </Mui.Box>

                                        <Mui.Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }} pt="20px">
                                            <Mui.Typography variant="body1">
                                                {t('vehicleEntrance.withoutPlate.carsData.from')}
                                            </Mui.Typography>
                                            <Mui.Typography variant="h6">{locationStartValue}</Mui.Typography>

                                            <Mui.Typography variant="body1" sx={{ pl: 2 }}>
                                                {t('vehicleEntrance.withoutPlate.carsData.to')}
                                            </Mui.Typography>
                                            <Mui.Typography variant="h6">{locationEndValue}</Mui.Typography>
                                        </Mui.Box>

                                        <Mui.Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }} pt="20px" >
                                            <Mui.Typography variant="body1">
                                            {t('vehicleEntrance.withoutPlate.carsData.effective')}
                                            </Mui.Typography>
                                            <Mui.Typography variant="h6">{vehicleData.effectiveEndLocation.name}</Mui.Typography>
                                        </Mui.Box>

                                        <Mui.Box sx={{ mt: 5 }}>
                                            <AutoComplete
                                                placeholder={t('vehicleEntrance.withoutPlate.carsData.transferType')}
                                                label={t('vehicleEntrance.withoutPlate.carsData.transferType')}
                                                fullWidth
                                                options={types}
                                                {...form1.register('transferType', {
                                                    required: t(
                                                        'vehicleAvailability.status.newBlock.inputs.reasonCodeRequired',
                                                    ),
                                                })}
                                                value={form1.watch('transferType') || ''}
                                                onChange={(newStatusValue: any) => {
                                                    form1.setValue('transferType', newStatusValue);
                                                }}
                                                error={form1.formState.errors?.transferType?.message?.toString()}
                                            />
                                        </Mui.Box>
                                    </Mui.CardContent>
                                </Mui.Card>
                            </Mui.Grid>

                            <Mui.Grid item md={6} xs={12}>
                                <Mui.Card>
                                    <Mui.CardContent>
                                        <Mui.Typography variant="h5">Drivers</Mui.Typography>
                                        <Mui.Autocomplete
                                            multiple
                                            sx={{ width: '100% !important' }}
                                            id="drivers"
                                            value={drivers}
                                            onChange={(event, newValue: any) => {
                                                setDrivers(newValue);
                                            }}
                                            options={allDrivers}
                                            getOptionLabel={(option) => option.label}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Mui.Chip label={option.label} {...getTagProps({ index })} />
                                                ))
                                            }
                                            style={{ width: 500 }}
                                            renderInput={(params) => (
                                                <Mui.TextField
                                                    {...params}
                                                    placeholder={
                                                        drivers.length === 0
                                                            ? t('vehicleEntrance.withoutPlate.confirm.selectDriver')
                                                            : ''
                                                    }
                                                />
                                            )}
                                        />
                                        <form id="allPlates" onSubmit={form1.handleSubmit(onSubmit)}>
                                            <Mui.Grid sx={{ mt: 4 }}>
                                                {plates.map((item: VehicleEntranceExternalDDTType, index: any) => {
                                                    return (
                                                        <Mui.Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderTop: 1,
                                                                borderBottom: 1,
                                                                borderColor: 'rgba(1, 30, 65, 0.1)',
                                                                paddingTop: 2,
                                                                paddingBottom: 2,
                                                            }}
                                                        >
                                                            <Mui.Typography mr={4} variant="h6">
                                                                {index + 1}
                                                            </Mui.Typography>
                                                            <Mui.Typography ml={1} variant="body">
                                                                {item.plate}
                                                            </Mui.Typography>
                                                            <Mui.Typography ml={4} variant="body">
                                                                {`${item.model.brand.descr} ${item.model.descr}`}
                                                            </Mui.Typography>
                                                            <Mui.Typography ml={4} mr="auto" variant="body">
                                                                {item.position}
                                                            </Mui.Typography>

                                                            <Mui.Button
                                                                onClick={() => onOpenCheckIn(item)}
                                                                disabled={checkedInVehicles.includes(item.plate)}
                                                            >
                                                                {t("vehicleEntrance.withoutPlate.confirm.checkInButton")}
                                                            </Mui.Button>

                                                            <Mui.IconButton
                                                                onClick={() => {
                                                                    setPlates(
                                                                        plates.filter(
                                                                            (vehicle: VehicleEntranceExternalDDTType) => vehicle.plate !== item.plate,
                                                                        ),
                                                                    );
                                                                }}
                                                                sx={{ marginLeft: 4 }}
                                                            >
                                                                <DeleteIcon />
                                                            </Mui.IconButton>

                                                        </Mui.Box>
                                                    );
                                                })}

                                                <Mui.Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        borderTop: 1,
                                                        borderBottom: 1,
                                                        borderColor: 'rgba(1, 30, 65, 0.1)',
                                                        paddingTop: 2,
                                                        paddingBottom: 2,
                                                    }}
                                                >
                                                    <Mui.Typography mr={4} variant="h6">
                                                        {plates.length + 1}
                                                    </Mui.Typography>
                                                    <Mui.IconButton color="accent" onClick={() => setVisibleDialog(true)}>
                                                        <AddIcon />
                                                        {t('vehicleEntrance.withoutPlate.carsData.addVehicle')}
                                                    </Mui.IconButton>
                                                </Mui.Box>
                                            </Mui.Grid>

                                            <Mui.Button
                                                // disabled={plates.length === 0 || plates.length !== checkedInVehicles.length}
                                                fullWidth
                                                variant="contained"
                                                style={{
                                                    marginTop: 10,
                                                }}
                                                onClick={drivers.length === 0 ? checkDrivers : form1.handleSubmit(onSubmit)}
                                            >
                                                {t('vehicleEntrance.withoutPlate.carsData.buttonTitle')}
                                            </Mui.Button>

                                            <Mui.Dialog
                                                open={open}
                                                onClose={handleClose}
                                                aria-labelledby="alert-dialog-title"
                                                aria-describedby="alert-dialog-description"
                                            >
                                                <Mui.DialogTitle>
                                                    <Mui.Box sx={{ display: 'flex' }}>
                                                        <InfoSharpIcon
                                                            sx={{
                                                                color: '#C8017D',
                                                                marginRight: '17px',
                                                            }}
                                                        />

                                                        <Mui.Typography fontWeight={'bold'} color={'primary'}>
                                                            {dialogData.title}
                                                        </Mui.Typography>
                                                    </Mui.Box>

                                                    <Mui.IconButton
                                                        aria-label="close"
                                                        onClick={handleClose}
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 8,
                                                            top: 8,
                                                        }}
                                                    >
                                                        <CloseIcon />
                                                    </Mui.IconButton>
                                                </Mui.DialogTitle>

                                                <Mui.DialogContent>
                                                    {dialogData.bodyTitle && (
                                                        <Mui.DialogContentText
                                                            id="alert-dialog-description"
                                                            style={{ marginBottom: 20 }}
                                                        >
                                                            {dialogData.bodyTitle}
                                                        </Mui.DialogContentText>
                                                    )}

                                                    <Mui.DialogContentText id="alert-dialog-description">
                                                        {dialogData.body}
                                                    </Mui.DialogContentText>
                                                </Mui.DialogContent>
                                                <Mui.DialogActions>
                                                    <Mui.Button
                                                        type="submit"
                                                        variant="contained"
                                                        onClick={form1.handleSubmit(onSubmit, () => setOpen(false))}
                                                    >
                                                        {t('vehicleExit.status.confirmVehicle')}
                                                    </Mui.Button>

                                                    <Mui.Button
                                                        onClick={handleClose}
                                                        style={{ color: '#d12c93', backgroundColor: 'white' }}
                                                    >
                                                        {t('vehicleExit.status.cancelVehicle')}
                                                    </Mui.Button>
                                                </Mui.DialogActions>
                                            </Mui.Dialog>
                                        </form>
                                    </Mui.CardContent>
                                </Mui.Card>
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.Paper>
                )}

                <Mui.Dialog
                    open={visibleDialog || confirm}
                    onClose={() => {
                        if (visibleDialog) {
                            setVisibleDialog(false);
                        } else {
                            setConfirm(false);
                        }
                    }}
                    aria-labelledby="responsive-dialog-title"
                    disableEscapeKeyDown={true}
                    fullWidth={true}
                    fullScreen={confirm}
                >
                    {visibleDialog && (
                        <>
                            <Mui.DialogTitle
                                id="responsive-dialog-title"
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                                {t('vehicleEntrance.withoutPlate.carsData.addVehicle')}
                            </Mui.DialogTitle>

                            <form id="addPlate" onSubmit={form2.handleSubmit(onPlateAddSubmit)}>
                                <Mui.DialogContent style={{ paddingLeft: 20, paddingRight: 20 }}>
                                    <AutoComplete
                                        options={allPlatesOptions()}
                                        variant="standard"
                                        fullWidth
                                        label={t('vehicleEntrance.status.plate')}
                                        placeholder={t('vehicleExit.search.content.inputs.plate.placeholder')}
                                        {...form2.register(`plate`, {
                                            required: t('vehicleEntrance.status.driverError'),
                                        })}
                                        value={form2.watch('plate') || ''}
                                        onChange={(value: any) => {
                                            form2.setValue(`plate`, value);
                                        }}
                                        error={Boolean(form2.formState.errors.plate)}
                                        helperText={form2.formState.errors['plate']?.message?.toString()}
                                    />
                                    <Mui.TextField
                                        variant="standard"
                                        fullWidth
                                        label={t('vehicleEntrance.status.position')}
                                        placeholder={t('vehicleEntrance.status.positionLabel')}
                                        {...form2.register('position')}
                                        value={form2.watch(`position`)}
                                        onChange={(event) => {
                                            form2.setValue(`position`, event.target.value);
                                        }}
                                        sx={{ marginBottom: 2 }}
                                        inputProps={{
                                            style: {
                                                padding: 5,
                                                paddingLeft: 10,
                                            },
                                        }}
                                        error={Boolean(form2.formState.errors.position)}
                                    />
                                </Mui.DialogContent>

                                <Mui.DialogActions sx={{ flexDirection: 'column' }}>
                                    <Mui.Button
                                        fullWidth
                                        variant="contained"
                                        type="submit"
                                        style={{
                                            color: 'white',
                                            backgroundColor: '#d12c93',
                                            marginBottom: 20,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {t('vehicleEntrance.withoutPlate.carsData.addButtonLabel')}
                                    </Mui.Button>

                                    <Mui.Button
                                        fullWidth
                                        variant="text"
                                        style={{ color: '#d12c93', fontWeight: 800, marginLeft: 0 }}
                                        onClick={() => setVisibleDialog(false)}
                                    >
                                        {t('vehicleEntrance.withoutPlate.carsData.back')}
                                    </Mui.Button>
                                </Mui.DialogActions>
                            </form>
                        </>
                    )}

                    {confirm && (
                        <Mui.DialogContent
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexDirection: 'column',
                                paddingLeft: 8,
                                paddingRight: 8,
                            }}
                        >
                            <Mui.Typography variant="h4" whiteSpace="pre-line" textAlign="center" mb={5}>
                                {t('vehicleEntrance.withoutPlate.carsData.successModalTitle')}
                            </Mui.Typography>

                            <Mui.Button
                                variant="contained"
                                style={{ color: 'white', backgroundColor: '#d12c93' }}
                                onClick={() => navigate('/')}
                            >
                                {t('vehicleEntrance.withoutPlate.carsData.successButtonTitle')}
                            </Mui.Button>
                        </Mui.DialogContent>
                    )}
                </Mui.Dialog>

                <Mui.Dialog open={showDownloadModal}>
                    <Mui.DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                        <InfoRounded color="secondary" sx={{ fontSize: '20px', marginRight: 2 }} />
                        {t('vehicleExit.confirm.dialog.title')}
                    </Mui.DialogTitle>

                    <Mui.DialogContent>{t('vehicleExit.confirm.dialog.body')}</Mui.DialogContent>

                    <Mui.DialogActions>
                        <Mui.Button variant="contained" size="small" onClick={() => downloadDeliveryNote()}>
                            {t('vehicleExit.confirm.dialog.buttons.yes')}
                        </Mui.Button>

                        <Mui.Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                setShowDownloadModal(false);
                                navigate(`/operation/transfer/vehiclesEntrance`);
                            }}
                        >
                            {t('vehicleExit.confirm.dialog.buttons.no')}
                        </Mui.Button>
                    </Mui.DialogActions>
                </Mui.Dialog>

                <OpenTransferPlatesDialog 
                    open={transferPlatesDialog.open} 
                    data={transferPlatesDialog.data} 
                    onClose={() => {
                        setTransferPlatesDialog({
                            open: false,
                            data: transferPlatesDialog.data
                        })
                    }} 
                    onConfirm={() => {
                        setTransferPlatesDialog({
                            open: false,
                            data: transferPlatesDialog.data
                        });

                        setLoading(true);
                        handleConfirmEntrance(form1.getValues(), transferPlatesDialog.data.map(d => d.number))
                        
                    }}                
                />
            </Mui.Box>

            {selectedVehicle && (
                <CheckInOutWrapper
                    brandDescription={selectedVehicle.model.brand.descr}
                    callbackButtonLabel={t("vehicleEntrance.withoutPlate.confirm.checkInCBButton")}
                    currentBatteryPercentage={selectedVehicle.currentElectricTankFilling}
                    currentFuelLevelEighths={selectedVehicle.currentTermicTankFilling}
                    currentKms={selectedVehicle.kms}
                    enginePowerGroupCode={selectedVehicle.enginePowerCode}
                    modelDescription={selectedVehicle.model.descr}
                    oprCallback={onConfirmCheckIn}
                    plate={selectedVehicle.plate}
                    carClass={selectedVehicle.carClass.descr}
                    bypassDamagesEquipments={false}
					viewType='ddt'
                />
            )}  
        </>
    );
};
export default VehicleEntranceExternalDDTConfirm;
